"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Registration() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    number: "01",
    title: "Initial Inquiry",
    body: "Begin your journey by scheduling a personal tour of our Parkwoods-Donalda campus. We invite you to observe our classrooms and discuss your child's unique developmental needs.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/registration_tour.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/registration_tour.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/registration_tour.png-wvc-sizes",
    alt: "Parent and child touring the Montessori classroom"
  }, {
    number: "02",
    title: "Application",
    body: "Complete our formal application package. This includes sharing your child's history, immunization records, and confirming their readiness for our independent learning environment.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/registration_forms.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/registration_forms.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/registration_forms.png-wvc-sizes",
    alt: "Montessori enrollment forms on a wooden desk"
  }, {
    number: "03",
    title: "Family Interview",
    body: "We meet with both parents and the prospective student to ensure our Montessori philosophy aligns with your family's values and educational goals for your child.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/registration_interview.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/registration_interview.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/registration_interview.webp-wvc-sizes",
    alt: "Teacher speaking with a family in a warm classroom"
  }, {
    number: "04",
    title: "Welcome Home",
    body: "Upon acceptance, we finalize your enrollment agreement and provide our comprehensive parent handbook to prepare your little one for their first day at Little Acorns.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/registration_welcome.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/registration_welcome.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/registration_welcome.png-wvc-sizes",
    alt: "Child playing with Montessori materials"
  }];
  return <section data-section-id="4394" id="registration-steps" ref={sectionRef} className="dark bg-background text-foreground py-24 md:py-32 lg:py-44 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div className="text-center mb-20 md:mb-28">
          <p className="font-default text-xs tracking-[0.3em] uppercase mb-8 transition-all duration-1000" style={{
          color: "var(--chart-3)",
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(20px)"
        }}>
            Enrollment Path
          </p>
          <h2 className="font-default text-5xl md:text-6xl lg:text-[64px] leading-[1.05] font-semibold mb-8 transition-all duration-1000 delay-150" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(20px)"
        }}>
            Our Enrollment <em className="italic font-serif font-medium" style={{
            color: "var(--chart-3)"
          }}>Journey</em>.
          </h2>
          <p className="font-default text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed transition-all duration-1000 delay-300" style={{
          color: "var(--muted-foreground)",
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(20px)"
        }}>
            A thoughtful, step-by-step process designed to welcome your child into our community and ensure a seamless transition into the Montessori method.
          </p>
          <div className="mx-auto h-px transition-all duration-1000 delay-500" style={{
          backgroundColor: "var(--chart-3)",
          width: isVisible ? "120px" : "0px"
        }} />
        </div>

        {}
        <div className="relative">
          {}
          <div className="hidden lg:block absolute top-[60px] left-0 right-0 h-px overflow-hidden pointer-events-none">
            <div className="h-px origin-left transition-transform ease-out" style={{
            backgroundColor: "var(--chart-3)",
            opacity: 0.4,
            transform: isVisible ? "scaleX(1)" : "scaleX(0)",
            transitionDuration: "1400ms"
          }} />
          </div>

          {}
          <div className="lg:hidden absolute top-0 bottom-0 left-6 w-px overflow-hidden pointer-events-none">
            <div className="w-px h-full origin-top transition-transform ease-out" style={{
            backgroundColor: "var(--chart-3)",
            opacity: 0.4,
            transform: isVisible ? "scaleY(1)" : "scaleY(0)",
            transitionDuration: "1400ms"
          }} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 lg:gap-8 relative">
            {steps.map((step, i) => {
            const delay = 500 + i * 200;
            return <div key={i} data-index={i} className="relative pl-16 lg:pl-0 transition-all ease-out" style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(30px)",
              transitionDuration: "900ms",
              transitionDelay: `${delay}ms`
            }}>
                  {}
                  <div className="absolute lg:top-[54px] top-0 lg:left-[-6px] left-[18px] w-3 h-3 rotate-45 z-10" style={{
                backgroundColor: "var(--chart-3)"
              }} />

                  {}
                  <div className="font-default text-[72px] md:text-[88px] lg:text-[96px] leading-none font-bold mb-6 lg:mb-8 tracking-tight" style={{
                color: "transparent",
                WebkitTextStroke: "1px var(--chart-3)"
              }}>
                    {step.number}
                  </div>

                  {}
                  <h3 className="font-default text-2xl font-semibold mb-4">
                    {step.title}
                  </h3>

                  {}
                  <p className="font-default text-base leading-relaxed mb-8" style={{
                color: "var(--muted-foreground)"
              }}>
                    {step.body}
                  </p>

                  {}
                  <div className="relative w-full overflow-hidden rounded-lg" style={{
                aspectRatio: "4/5"
              }}>
                    <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-all duration-700" />
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div className="mt-24 md:mt-32 text-center transition-all duration-1000" style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(20px)",
        transitionDelay: "1500ms"
      }}>
          <Button asChild variant="link" className="font-default text-sm tracking-[0.1em] uppercase p-0 h-auto hover:no-underline group" style={{
          color: "var(--foreground)"
        }}>
            <Link to="/FeesEnrollmentPage" className="inline-flex items-center gap-3">
              <span className="border-b border-current pb-1 transition-colors group-hover:opacity-70">
                Start Application
              </span>
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}